import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Loader, AlertTriangle, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center h-full text-slate-600 dark:text-slate-300">
        <Loader className="animate-spin h-12 w-12 mb-4" />
        <p className="text-lg">Carregando PDF...</p>
    </div>
);

const ErrorDisplay = ({ message, onDownload }) => (
    <div className="flex flex-col items-center justify-center h-full text-center p-8 text-red-600 dark:text-red-400">
        <AlertTriangle className="h-12 w-12 mb-4" />
        <p className="text-lg font-semibold">Falha ao carregar o PDF</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{message}</p>
        <button 
            onClick={onDownload} 
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
            <Download size={18} />
            Tentar baixar o arquivo
        </button>
    </div>
);

const PDFViewerModal = ({ isOpen, onClose, onDownload, fileUrl, fileName }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(window.innerWidth < 640 ? 0.6 : 1.0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setPageNumber(1);
            const isMobile = window.innerWidth < 640;
            setScale(isMobile ? 0.6 : 1.0);
            setIsLoading(true);
            setError(null);
            setNumPages(null);
        }
    }, [isOpen]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setIsLoading(false);
        setError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error("Erro ao carregar o documento PDF:", error);
        setError(`Ocorreu um erro ao tentar exibir o arquivo. (${error.message})`);
        setIsLoading(false);
    };

    const goToPrevPage = () => setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
    const goToNextPage = () => setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages));
    const zoomIn = () => setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
    const zoomOut = () => setScale(prevScale => Math.max(prevScale - 0.2, 0.5));

    const options = useMemo(() => ({
        cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }), []);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 z-[1060] flex justify-center items-center p-2 sm:p-4 animate-fade-in">
            <div className="bg-slate-100 dark:bg-slate-900 rounded-lg shadow-2xl w-full h-full flex flex-col" role="dialog" aria-modal="true">
                <header className="flex items-center justify-between p-3 border-b border-slate-300 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-800 rounded-t-lg">
                    <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100 truncate pr-2" title={fileName}>
                        {fileName}
                    </h2>
                    <div className="flex items-center gap-1 sm:gap-2">
                        {numPages && (
                            <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                <button onClick={goToPrevPage} disabled={pageNumber <= 1} className="p-1.5 rounded-full disabled:opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"><ChevronLeft size={20} /></button>
                                <span>{pageNumber} de {numPages}</span>
                                <button onClick={goToNextPage} disabled={pageNumber >= numPages} className="p-1.5 rounded-full disabled:opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"><ChevronRight size={20} /></button>
                            </div>
                        )}
                         <div className="h-6 border-l border-slate-300 dark:border-slate-600 mx-1"></div>
                        <button onClick={zoomOut} aria-label="Diminuir zoom" className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" title="Diminuir zoom"><ZoomOut size={20} /></button>
                        <button onClick={zoomIn} aria-label="Aumentar zoom" className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" title="Aumentar zoom"><ZoomIn size={20} /></button>
                        <div className="h-6 border-l border-slate-300 dark:border-slate-600 mx-1"></div>
                        <button onClick={onDownload} aria-label="Baixar Arquivo" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" title="Baixar Arquivo">
                            <Download size={16} />
                            <span className="hidden md:inline">Baixar</span>
                        </button>
                        <button onClick={onClose} aria-label="Fechar" className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" title="Fechar">
                            <X size={22} className="text-slate-600 dark:text-slate-300" />
                        </button>
                    </div>
                </header>
                <div className="flex-grow p-1 sm:p-2 bg-slate-200 dark:bg-slate-800/50 overflow-auto">
                    <div className="w-full h-full flex items-start justify-center">
                        {fileUrl ? (
                            <Document
                                file={fileUrl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={<LoadingSpinner />}
                                error={<ErrorDisplay message={error} onDownload={onDownload} />}
                                options={options}
                            >
                               { !isLoading && !error && (
                                    <Page 
                                        pageNumber={pageNumber} 
                                        scale={scale}
                                        loading=""
                                        renderTextLayer={false}
                                    />
                                )}
                            </Document>
                        ) : (
                            <LoadingSpinner />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PDFViewerModal;
